import { Tab, Tabs } from '@blueprintjs/core'
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { CodeMirrorInput } from 'browser/components/atomic-elements/atoms/input/code-mirror-input'
import { EntityPreviewCard } from 'browser/components/atomic-elements/organisms/entity/entity-preview-card/entity-preview-card'
import ComponentsMap from 'browser/components'
import { MultiMixinRenderer } from 'shared-libs/components/entity/multi-mixin-renderer'
import { Entity } from 'shared-libs/models/entity'

const ENTITY_DETAIL_UI_SCHEMA_PATH = 'uiSchema.web.entityDetailSection'

/** Builds a lightweight (unresolved) Entity model from raw entity content; null on failure. */
export function toEntityModel(content: any): Entity | null {
  if (!content) {
    return null
  }
  try {
    return new Entity(content, apis)
  } catch {
    return null
  }
}

/** Keeps a failed entity preview (e.g. an unresolved schema) from taking down the whole card. */
class ResultErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }
  public static getDerivedStateFromError() {
    return { hasError: true }
  }
  public render() {
    if (this.state.hasError) {
      return <div className='c-placeholderColor'>(entity preview unavailable — see JSON)</div>
    }
    return this.props.children
  }
}

/**
 * Renders a transient entity with the real metadata-driven detail renderer instead of raw JSON.
 *
 * A transient (never-persisted) entity built with a bare `new Entity(content, apis)` has no resolved
 * mixin schemas, so the renderer would draw nothing. We resolve the mixin metadata through the store
 * first (async, read-only — `shouldCache=false` keeps this preview out of the identity map), then
 * render `uiSchema.web.entityDetailSection` via MultiMixinRenderer, which renders empty (rather than
 * throwing) for any mixin that doesn't define that path.
 */
class ResolvedEntityDetail extends React.Component<
  { content: any },
  { model: Entity | null; status: 'loading' | 'ready' | 'failed' }
> {
  private isMounted_ = false

  constructor(props) {
    super(props)
    this.state = { model: null, status: 'loading' }
  }

  public componentDidMount() {
    this.isMounted_ = true
    apis
      .getStore()
      .resolveRecords([_.cloneDeep(this.props.content)], /* shouldCache */ false)
      .then(([model]) => {
        if (this.isMounted_) {
          this.setState({ model, status: 'ready' })
        }
      })
      .catch(() => {
        if (this.isMounted_) {
          this.setState({ status: 'failed' })
        }
      })
  }

  public componentWillUnmount() {
    this.isMounted_ = false
  }

  public render() {
    const { model, status } = this.state
    if (status === 'loading') {
      return <div className='c-placeholderColor u-bumperTop--xs'>Rendering entity…</div>
    }
    if (status === 'failed' || !model) {
      // Fall back silently — the collapsed JSON is still available on the other tab.
      return null
    }
    // A plain full-width block: fills the pane width (the detail panel grows to the pane via flex)
    // while keeping the section's natural height. An earlier grid-block/grid-content wrapper
    // filled the width too, but its `flex: 1 1 0` collapsed the section's height in the maximized
    // flex-column layout, leaving the detail body nearly empty.
    return (
      <div
        className='w-100 u-bumperTop--xs'
        style={{ border: '1px solid #e0e0e0', borderRadius: 3, padding: 8 }}
      >
        <MultiMixinRenderer
          value={model}
          componentsMap={ComponentsMap}
          uiSchemaPath={ENTITY_DETAIL_UI_SCHEMA_PATH}
          // Render only the most-derived mixin's detail section, so an entity with several mixins
          // that each define entityDetailSection (e.g. carrierAppointment = calendar appointment +
          // carrier appointment) shows one consolidated section instead of a repeated one.
          shouldOverrideUiSchema={true}
          context={{ density: 'collapse', isHorizontalLayout: true, isStatic: true }}
          state={{ settings: apis.getSettings() }}
          actions={this}
          isEditable={false}
          onChangeComplete={_.noop}
        />
      </div>
    )
  }
}

/**
 * Read-only detail card for a single transient (in-memory, never-persisted) entity: an entity Details
 * view rendered through the app's metadata renderer — the same `entityDetailSection` the real
 * EntityDetailCard uses — plus a JSON tab. It is the transient parallel of EntityDetailCard with none
 * of its persisted-entity chrome (no header/share/menu, save, activity, or backend queries).
 *
 * Mount it with a `key` tied to the entity so switching selection resets the tab and re-resolves.
 * Caller-specific framing (e.g. a dry-run alias/action/valid header) belongs in the wrapper, not here.
 */
export function TransientEntityDetailCard({ content }: { content: any }) {
  const entityModel = toEntityModel(content)
  return (
    <div className='u-flexColumn' style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 10 }}>
        <Tabs id='transientEntityDetail' defaultSelectedTabId='details' renderActiveTabPanelOnly={true}>
          <Tab
            id='details'
            title='Details'
            panel={
              <div>
                {entityModel && (
                  <ResultErrorBoundary>
                    <EntityPreviewCard entity={entityModel} isClickable={false} />
                  </ResultErrorBoundary>
                )}
                {content && (
                  <ResultErrorBoundary>
                    <ResolvedEntityDetail content={content} />
                  </ResultErrorBoundary>
                )}
              </div>
            }
          />
          <Tab
            id='json'
            title='JSON'
            panel={
              <CodeMirrorInput
                value={JSON.stringify(content, null, 2)}
                language='json'
                onChange={_.noop}
              />
            }
          />
        </Tabs>
      </div>
    </div>
  )
}
